// Libs
import classNames from 'classnames/bind';
import {
  Button,
  Flex,
  Heading,
  IconDocumentLine,
  IconLinkLine,
  IconRefreshLine,
  Spinner,
  Table,
  Text,
  TextInput,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import ReactPlayer from 'react-player';
import moment from 'moment';
// Components, Layouts, Pages
import Information from '~/pages/student/home/component/information/Information';
import { BasePagination } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { useCanvasSessionExpired } from '~/utils/hooks/canvasSession';
import { getCourseMaterials } from '~/thunks/course/courseThunk';
import {
  COURSE_ACTIVATION_MESSAGE_KEYS,
  DATE_FORMAT,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_PAGE,
  MATERIAL_ARCHIVE_MAX_AUTO_RETRIES,
  MATERIAL_ARCHIVE_RETRY_DELAY_MS,
} from '~/utils/constants/common';
import { MaterialTypeEnum } from '~/utils/enum/common';
import { IBaseErrorData } from '~/utils/interface/base';
import { ICourseMaterial, ICourseMaterialLesson, ICourseMaterials } from '~/utils/interface/course';
// Styles, images, icons
import styles from './MaterialArchive.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];

// Same check as the lesson page, which opens these links in its video player: they stay in the lesson.
const isVideoLink = (link: string) =>
  VIDEO_EXTENSIONS.some((extension) => link.toLowerCase().endsWith(extension)) || ReactPlayer.canPlay(link);

const MaterialArchive = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isCanvasSessionExpired = useCanvasSessionExpired();
  //#endregion Declare Hook

  //#region Declare State
  const [materials, setMaterials] = useState<ICourseMaterials | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<number>();
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [autoRetries, setAutoRetries] = useState<number>(0);
  //#endregion Declare State

  //#region Declare Memo
  const lessons = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const matches = (text: string) => text.toLowerCase().includes(query);

    return (materials?.lessons ?? [])
      .map((lesson) => {
        const lessonMaterials = lesson.materials.filter((material) => !isVideoLink(material.link));
        // A lesson whose name matches shows all its materials, otherwise only the matching ones
        if (!query || matches(lesson.name)) return { ...lesson, materials: lessonMaterials };
        return { ...lesson, materials: lessonMaterials.filter((material) => matches(material.title)) };
      })
      .filter((lesson) => lesson.materials.length > 0);
  }, [materials, searchText]);

  const pageCount = Math.max(1, Math.ceil(lessons.length / DEFAULT_LIMIT_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageLessons = lessons.slice((currentPage - 1) * DEFAULT_LIMIT_PAGE, currentPage * DEFAULT_LIMIT_PAGE);
  const isStillLoading = !!materials && !materials.isComplete;
  const activationMessageKey = errorCode ? COURSE_ACTIVATION_MESSAGE_KEYS[errorCode] : undefined;
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A partial answer: the backend keeps loading the missing files, so ask again a few times.
  useEffect(() => {
    if (!isStillLoading || autoRetries >= MATERIAL_ARCHIVE_MAX_AUTO_RETRIES) return;

    const timer = setTimeout(() => {
      setAutoRetries((count) => count + 1);
      handleGetMaterials(true);
    }, MATERIAL_ARCHIVE_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetMaterials = async (isAutoRetry = false) => {
    if (!isAutoRetry) {
      setIsLoading(true);
      setHasError(false);
      setErrorCode(undefined);
    }

    try {
      const response = await dispatch(getCourseMaterials()).unwrap();
      setMaterials(response.data);
    } catch (error) {
      if (isAutoRetry) {
        // Keep the list already shown, and offer the Retry button instead of more automatic retries
        setAutoRetries(MATERIAL_ARCHIVE_MAX_AUTO_RETRIES);
        return;
      }
      setHasError(true);
      setErrorCode((error as AxiosError<IBaseErrorData>)?.response?.data?.code);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setAutoRetries(0);
    handleGetMaterials();
  };

  const handleChangeSearch = (value: string) => {
    setSearchText(value);
    setPage(DEFAULT_PAGE);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderRetryButton = () => (
    <Button onClick={handleRetry} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
      {t('retry_button_label', { ns: 'common/base' })}
    </Button>
  );

  const renderLessonDate = (lesson: ICourseMaterialLesson) => (
    <Text variant='contentSmall' color='secondary'>
      {moment.utc(lesson.date).local().format(DATE_FORMAT.DATE)}
    </Text>
  );

  const renderMaterialLinks = (lessonMaterials: ICourseMaterial[]) => (
    <div className={cx('materialList')}>
      {lessonMaterials.map((material) => (
        <a key={material.id} href={material.link} className={cx('itemLink')} target='_blank' rel='noopener noreferrer'>
          {material.type === MaterialTypeEnum.FILE ? <IconDocumentLine /> : <IconLinkLine />}
          <Text variant='content'>{material.title}</Text>
        </a>
      ))}
    </div>
  );

  const renderNotice = () => {
    if (!isStillLoading) return null;

    if (autoRetries < MATERIAL_ARCHIVE_MAX_AUTO_RETRIES) {
      return (
        <Flex alignItems='center' gap='small' margin='0 0 small 0'>
          <Spinner size='x-small' renderTitle={t('material_archive_loading_more')} />
          <Text variant='contentSmall'>{t('material_archive_loading_more')}</Text>
        </Flex>
      );
    }

    return (
      <Flex alignItems='center' gap='small' margin='0 0 small 0' wrap='wrap'>
        <Text variant='contentSmall'>{t('material_archive_incomplete')}</Text>
        {renderRetryButton()}
      </Flex>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' renderTitle={t('material_archive_title')} />
        </div>
      );
    }

    if (hasError) {
      return (
        <div className={cx('centerContainer')}>
          {activationMessageKey || isCanvasSessionExpired ? (
            <Text variant='contentSmall'>
              {activationMessageKey
                ? t(activationMessageKey, { ns: 'common/home' })
                : t('canvas_session_expired_card_message', { ns: 'common/base' })}
            </Text>
          ) : (
            renderRetryButton()
          )}
        </div>
      );
    }

    if (lessons.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Text variant='contentSmall'>
            {searchText.trim() ? t('material_archive_no_results') : t('material_archive_no_data')}
          </Text>
        </div>
      );
    }

    return (
      <>
        <div className={cx('desktopOnly')}>
          <Table layout='auto' caption={t('material_archive_title')}>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='Lesson' width='35%'>
                  {t('material_archive_lesson_column')}
                </Table.ColHeader>
                <Table.ColHeader id='Materials' width='65%'>
                  {t('material_archive_materials_column')}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>

            <Table.Body>
              {pageLessons.map((lesson) => (
                <Table.Row key={lesson.id}>
                  <Table.Cell>
                    <div className={cx('lessonInfo')}>
                      <Text variant='content'>{lesson.name}</Text>
                      {renderLessonDate(lesson)}
                    </div>
                  </Table.Cell>
                  <Table.Cell>{renderMaterialLinks(lesson.materials)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        <div className={cx('mobileOnly')}>
          {pageLessons.map((lesson) => (
            <div key={lesson.id} className={cx('lessonCard')}>
              <div className={cx('lessonInfo')}>
                <Text variant='contentImportant'>{lesson.name}</Text>
                {renderLessonDate(lesson)}
              </div>
              {renderMaterialLinks(lesson.materials)}
            </div>
          ))}
        </div>

        <BasePagination currentPage={currentPage} hasMore={currentPage < pageCount} onChange={setPage} />
      </>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <div className={cx('leftSection')}>
          <div className={cx('topBox')}>{<Information hideIcons={true} />}</div>
        </div>

        <div className={cx('rightSection')}>
          <div className={cx('titleContainer')}>
            <img
              src={icons.commonIconMaterialArchive}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('iconTitle')}
            />
            <Heading level='h3'>{t('material_archive_title')}</Heading>
          </div>
          {materials?.courseName && <Text variant='contentSmall'>{materials.courseName}</Text>}

          {!isLoading && !hasError && (
            <div className={cx('searchBar')}>
              <TextInput
                renderLabel=''
                placeholder={t('material_archive_search_placeholder')}
                value={searchText}
                onChange={(e) => handleChangeSearch(e.target.value)}
              />
            </div>
          )}

          {!isLoading && !hasError && renderNotice()}

          <div className={cx('tableContainer')}>{renderContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default MaterialArchive;
